import { Alert, Card, Col, Row, Typography } from "antd";
import ECharts from "echarts-for-react";
import {
    ANCHO_P1_MM,
    ANCHO_P2_MM,
    CENTRO_P1_GLOBAL,
    CENTRO_P2_GLOBAL,
    LARGO_P1_MM,
    LARGO_P2_MM,
    copHistorialDePrueba,
} from "../../Paginas/Prueba/COP/Guardar/cop";

const COLOR_P1 = "#fa8c16";
const COLOR_P2 = "#1677ff";
const COLOR_TOTAL = "#f5222d";

const puntoValido = (p) => Array.isArray(p) && p.every(Number.isFinite);

/** Contorno de una plataforma como serie de línea cerrada. */
function serieRectangulo(nombre, cx, cy, ancho, largo) {
    const x0 = cx - ancho / 2;
    const x1 = cx + ancho / 2;
    const y0 = cy - largo / 2;
    const y1 = cy + largo / 2;
    return {
        name: nombre,
        type: "line",
        symbol: "none",
        silent: true,
        data: [
            [x0, y0],
            [x0, y1],
            [x1, y1],
            [x1, y0],
            [x0, y0],
        ],
        lineStyle: { color: "#8c8c8c", width: 1.5, type: "dashed" },
    };
}

function serieTrayectoria(nombre, puntos, color) {
    return {
        name: nombre,
        type: "line",
        symbol: "none",
        data: puntos,
        lineStyle: { color, width: 1.2 },
    };
}

function ejePlano(titulo, min, max) {
    return {
        type: "value",
        name: titulo,
        nameLocation: "middle",
        nameGap: 32,
        min,
        max,
    };
}

/**
 * Visualización de una prueba COP guardada (detalle `/prueba/:id`).
 * Replica las vistas del script Python: fuerza por plataforma vs tiempo,
 * COP locales, COP combinado en vista superior y COP combinado en el tiempo.
 */
export function GraficaCOP({ prueba, mainChartRef }) {
    const sistema = prueba?.sistema_de_pesos_y_tiempos ?? [];
    const copH = copHistorialDePrueba(prueba);

    const tiempos = sistema.map((s) => Number(s?.tiempo ?? 0).toFixed(2));
    const fzP1 = sistema.map((s) => s?.peso?.p1 ?? s?.peso?.izq ?? 0);
    const fzP2 = sistema.map((s) => s?.peso?.p2 ?? s?.peso?.der ?? 0);
    const fzTotal = sistema.map((s) => s?.peso?.combinada ?? 0);

    const opcionesFuerza = {
        tooltip: { trigger: "axis" },
        legend: { data: ["P1", "P2", "Combinada"] },
        grid: { left: 62, right: 20, top: 42, bottom: 72 },
        dataZoom: [{ type: "inside" }, { type: "slider", height: 20, bottom: 6 }],
        xAxis: {
            type: "category",
            name: "Tiempo (s)",
            nameLocation: "middle",
            nameGap: 30,
            data: tiempos,
        },
        yAxis: {
            type: "value",
            name: "Fuerza (N)",
            nameLocation: "middle",
            nameGap: 48,
        },
        series: [
            { name: "P1", type: "line", symbol: "none", data: fzP1, lineStyle: { color: COLOR_P1 } },
            { name: "P2", type: "line", symbol: "none", data: fzP2, lineStyle: { color: COLOR_P2 } },
            {
                name: "Combinada",
                type: "line",
                symbol: "none",
                data: fzTotal,
                lineStyle: { color: COLOR_TOTAL },
            },
        ],
    };

    if (!copH.length) {
        return (
            <>
                <Card title="Fuerza vertical" size="small">
                    <div style={{ height: 420 }}>
                        <ECharts
                            ref={mainChartRef}
                            theme="dark2"
                            option={opcionesFuerza}
                            style={{ width: "100%", height: "100%" }}
                        />
                    </div>
                </Card>
                <br />
                <Alert
                    type="warning"
                    showIcon
                    message="Sin trayectorias COP"
                    description="Esta repetición se grabó con la trama antigua (2 fuerzas agregadas). Para ver los centros de presión se necesita la trama de 8 sensores + TIEMPO_MS."
                />
            </>
        );
    }

    const trayP1Global = copH.filter((r) => puntoValido(r.copP1Global)).map((r) => r.copP1Global);
    const trayP2Global = copH.filter((r) => puntoValido(r.copP2Global)).map((r) => r.copP2Global);
    const trayTotal = copH.filter((r) => puntoValido(r.copTotal)).map((r) => r.copTotal);
    const trayP1Local = copH.filter((r) => puntoValido(r.copP1Local)).map((r) => r.copP1Local);
    const trayP2Local = copH.filter((r) => puntoValido(r.copP2Local)).map((r) => r.copP2Local);

    const margenGlobal = 30;
    const xMinGlobal = Math.min(CENTRO_P1_GLOBAL[0] - ANCHO_P1_MM / 2, CENTRO_P2_GLOBAL[0] - ANCHO_P2_MM / 2) - margenGlobal;
    const xMaxGlobal = Math.max(CENTRO_P1_GLOBAL[0] + ANCHO_P1_MM / 2, CENTRO_P2_GLOBAL[0] + ANCHO_P2_MM / 2) + margenGlobal;
    const yMinGlobal = Math.min(CENTRO_P1_GLOBAL[1] - LARGO_P1_MM / 2, CENTRO_P2_GLOBAL[1] - LARGO_P2_MM / 2) - margenGlobal;
    const yMaxGlobal = Math.max(CENTRO_P1_GLOBAL[1] + LARGO_P1_MM / 2, CENTRO_P2_GLOBAL[1] + LARGO_P2_MM / 2) + margenGlobal;

    const opcionesGlobal = {
        tooltip: { trigger: "axis" },
        legend: { top: 5, data: ["COP P1", "COP P2", "COP combinado"] },
        grid: { left: 62, right: 20, top: 42, bottom: 40 },
        xAxis: ejePlano("ML (mm)", xMinGlobal, xMaxGlobal),
        yAxis: ejePlano("AP (mm)", yMinGlobal, yMaxGlobal),
        series: [
            serieRectangulo("Plataforma 1", CENTRO_P1_GLOBAL[0], CENTRO_P1_GLOBAL[1], ANCHO_P1_MM, LARGO_P1_MM),
            serieRectangulo("Plataforma 2", CENTRO_P2_GLOBAL[0], CENTRO_P2_GLOBAL[1], ANCHO_P2_MM, LARGO_P2_MM),
            serieTrayectoria("COP P1", trayP1Global, COLOR_P1),
            serieTrayectoria("COP P2", trayP2Global, COLOR_P2),
            serieTrayectoria("COP combinado", trayTotal, COLOR_TOTAL),
        ],
    };

    const margenLocal = 25;
    const opcionesLocal = (titulo, trayectoria, color) => ({
        tooltip: { trigger: "axis" },
        legend: { top: 5, data: [titulo] },
        grid: { left: 58, right: 16, top: 42, bottom: 40 },
        xAxis: ejePlano("ML (mm)", -ANCHO_P1_MM / 2 - margenLocal, ANCHO_P1_MM / 2 + margenLocal),
        yAxis: ejePlano("AP (mm)", -LARGO_P1_MM / 2 - margenLocal, LARGO_P1_MM / 2 + margenLocal),
        series: [
            serieRectangulo("Plataforma", 0, 0, ANCHO_P1_MM, LARGO_P1_MM),
            serieTrayectoria(titulo, trayectoria, color),
        ],
    });

    const tiemposCOP = copH.map((r) => Number(r?.tiempo ?? 0).toFixed(2));
    const mlTotal = copH.map((r) => (Number.isFinite(r?.copTotal?.[0]) ? r.copTotal[0] : null));
    const apTotal = copH.map((r) => (Number.isFinite(r?.copTotal?.[1]) ? r.copTotal[1] : null));

    const opcionesTemporal = {
        tooltip: { trigger: "axis" },
        legend: { top: 5, data: ["ML", "AP"] },
        grid: { left: 62, right: 20, top: 42, bottom: 72 },
        dataZoom: [{ type: "inside" }, { type: "slider", height: 20, bottom: 6 }],
        xAxis: {
            type: "category",
            name: "Tiempo (s)",
            nameLocation: "middle",
            nameGap: 30,
            data: tiemposCOP,
        },
        yAxis: {
            type: "value",
            name: "Desplazamiento (mm)",
            nameLocation: "middle",
            nameGap: 52,
        },
        series: [
            { name: "ML", type: "line", symbol: "none", data: mlTotal, lineStyle: { color: COLOR_P2 } },
            { name: "AP", type: "line", symbol: "none", data: apTotal, lineStyle: { color: COLOR_P1 } },
        ],
    };

    return (
        <>
            <Card title="Suma de los sensores por plataforma" size="small">
                <div style={{ height: 420 }}>
                    <ECharts
                        ref={mainChartRef}
                        theme="dark2"
                        option={opcionesFuerza}
                        style={{ width: "100%", height: "100%" }}
                    />
                </div>
            </Card>
            <br />
            <Card title="COP combinado — vista superior" size="small">
                <div style={{ height: 480 }}>
                    <ECharts
                        theme="dark2"
                        option={opcionesGlobal}
                        style={{ width: "100%", height: "100%" }}
                    />
                </div>
            </Card>
            <br />
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Card title="COP local — plataforma 1" size="small">
                        <div style={{ height: 380 }}>
                            <ECharts
                                theme="dark2"
                                option={opcionesLocal("COP P1", trayP1Local, COLOR_P1)}
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card title="COP local — plataforma 2" size="small">
                        <div style={{ height: 380 }}>
                            <ECharts
                                theme="dark2"
                                option={opcionesLocal("COP P2", trayP2Local, COLOR_P2)}
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                    </Card>
                </Col>
            </Row>
            <br />
            <Card title="COP combinado en el tiempo" size="small">
                <div style={{ height: 320 }}>
                    <ECharts
                        theme="dark2"
                        option={opcionesTemporal}
                        style={{ width: "100%", height: "100%" }}
                    />
                </div>
            </Card>
            <br />
            <Typography.Text type="secondary">
                {copH.length} muestras COP · P1 y P2 en N · trayectorias en mm (ML mediolateral, AP anteroposterior).
            </Typography.Text>
        </>
    );
}
